'use client';

import { ReactNode, createContext, useContext } from 'react';
import {
  VolumeDiscountDialogs,
  VolumeDiscountDialogsProps,
} from '@/app/[abTest]/[locale]/(spreadshirt)/cyo/assortment/detail/components/VolumeDiscountDialogs';

const VolumeDiscountContext = createContext<{ discounts: ProductTypeDiscount[] } | null>(null);

/** Reads the discounts provided by the nearest VolumeDiscountProvider. Throws outside of one —
 * VolumeDiscountActivator only ever makes sense paired with the dialog(s) the provider owns. */
export const useVolumeDiscount = () => {
  const context = useContext(VolumeDiscountContext);
  if (!context) {
    throw new Error('VolumeDiscountActivator must be rendered within a VolumeDiscountProvider');
  }
  return context;
};

export type VolumeDiscountProviderProps = VolumeDiscountDialogsProps & { children?: ReactNode };

/** Owns the volume-discount feature end to end: provides `discounts` to any number of
 * VolumeDiscountActivator instances placed anywhere in `children` (e.g. the price block and the
 * shipping/exchange USP row), and renders the shared tier-list/offer-calculator dialogs exactly
 * once. There is no way to render an activator without its dialog, and no way for the dialog to
 * be rendered more than once. Renders `children` (and nothing else) when the product has no
 * eligible volume discount. */
export const VolumeDiscountProvider = ({
  children,
  ...dialogProps
}: VolumeDiscountProviderProps) => {
  const { discounts } = dialogProps;
  const hasVolumeDiscount = discounts.some(({ from, value }) => from && value);

  return (
    <VolumeDiscountContext.Provider value={{ discounts }}>
      {children}
      {hasVolumeDiscount && <VolumeDiscountDialogs {...dialogProps} />}
    </VolumeDiscountContext.Provider>
  );
};
